import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { showToast } from './Toast'
import { fmtMoney } from '../lib/pricing'

// Zones — the dry-cleaning pricing grids.
//
// The MARGIN column is the point of this screen. At Miami prices a tie in
// Ponte Vedra sells for $6 and the subcontractor charges $7: a loss on every
// single piece, and nothing anywhere would have said so. Anything under 25%
// is flagged red.

const LOW_MARGIN_PCT = 25

export default function ZonesPanel({ items }) {
  const [zones, setZones] = useState([])
  const [prices, setPrices] = useState([])
  const [openZone, setOpenZone] = useState(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const [{ data: z }, { data: p }] = await Promise.all([
        supabase.from('pricing_zones').select('*').order('is_default', { ascending: false }),
        supabase.from('item_zone_prices').select('*'),
      ])
      setZones(z || [])
      setPrices(p || [])
    } catch (err) {
      showToast(err?.message || 'Could not load zones')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  if (loading) return <Card><p className="text-[13px] text-ink-2">Loading zones…</p></Card>

  return (
    <Card>
      <div className="mb-3">
        <h2 className="font-bricolage text-[15px] font-extrabold text-navy">Zones</h2>
        <p className="mt-0.5 text-[11px] text-ink-2">
          Dry cleaning is only offered where a zone allows it. Prices with no row here
          use the base item price.
        </p>
      </div>

      <div className="space-y-2">
        {zones.map((z) => {
          const rows = prices.filter((p) => p.zone_id === z.id)
          const open = openZone === z.id
          return (
            <div key={z.id} className="rounded-xl border border-line bg-white">
              <button
                type="button"
                onClick={() => setOpenZone(open ? null : z.id)}
                className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
              >
                <span className="min-w-0">
                  <span className="block font-bricolage text-[13px] font-extrabold text-navy">
                    {z.name}
                    {z.is_default && (
                      <span className="ml-2 rounded-full bg-navy/10 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-navy">
                        default
                      </span>
                    )}
                  </span>
                  <span className="block text-[11px] text-ink-2">
                    {z.dry_cleaning ? '✅ Dry cleaning offered' : '❌ No dry cleaning'}
                    {' · '}
                    {rows.filter((r) => r.available !== false).length} priced
                    {rows.some((r) => r.available === false)
                      ? ` · ${rows.filter((r) => r.available === false).length} hidden`
                      : ''}
                  </span>
                </span>
                <span aria-hidden className="shrink-0 text-ink-3">{open ? '▴' : '▾'}</span>
              </button>

              {open && (
                <ZoneGrid
                  zone={z}
                  rows={rows}
                  items={items}
                  onChanged={refresh}
                />
              )}
            </div>
          )
        })}
      </div>
    </Card>
  )
}

function ZoneGrid({ zone, rows, items, onChanged }) {
  const byItem = useMemo(() => new Map(rows.map((r) => [r.item_id, r])), [rows])
  // Only items that actually carry a zone row — the rest sit at base price
  // and would be noise here.
  const listed = useMemo(
    () => (items || []).filter((i) => byItem.has(i.id)),
    [items, byItem],
  )

  if (listed.length === 0) {
    return (
      <div className="border-t border-line px-3 py-3 text-[12px] text-ink-2">
        No overrides — every item uses its base price here.
      </div>
    )
  }

  return (
    <div className="overflow-x-auto border-t border-line">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-ink-3">
            <th className="px-3 py-2">Item</th>
            <th className="px-2 py-2 text-right">Base</th>
            <th className="px-2 py-2 text-right">Price</th>
            <th className="px-2 py-2 text-right">Cost</th>
            <th className="px-2 py-2 text-right">Margin</th>
            <th className="px-2 py-2 text-center">Offered</th>
          </tr>
        </thead>
        <tbody>
          {listed.map((item) => (
            <ZoneRow
              key={item.id}
              item={item}
              row={byItem.get(item.id)}
              zone={zone}
              onChanged={onChanged}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ZoneRow({ item, row, zone, onChanged }) {
  const [price, setPrice] = useState(((row.price_cents || 0) / 100).toFixed(2))
  const [cost, setCost] = useState(row.cost_cents == null ? '' : (row.cost_cents / 100).toFixed(2))
  const [busy, setBusy] = useState(false)

  const priceC = Math.round((Number(price) || 0) * 100)
  const costC = cost === '' ? null : Math.round((Number(cost) || 0) * 100)
  const marginPct = priceC > 0 && costC != null ? ((priceC - costC) / priceC) * 100 : null
  const low = marginPct != null && marginPct < LOW_MARGIN_PCT
  const dirty = priceC !== row.price_cents || costC !== (row.cost_cents ?? null)

  async function save(patch) {
    setBusy(true)
    try {
      const { error } = await supabase
        .from('item_zone_prices')
        .update(patch)
        .eq('item_id', item.id)
        .eq('zone_id', zone.id)
      if (error) throw error
      onChanged?.()
    } catch (err) {
      showToast(err?.message || 'Save failed')
    } finally { setBusy(false) }
  }

  return (
    <tr className="border-t border-line">
      <td className="px-3 py-2 text-navy">{item.name}</td>
      <td className="px-2 py-2 text-right text-ink-3">{fmtMoney(item.price_cents)}</td>
      <td className="px-2 py-2 text-right">
        <input
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          inputMode="decimal"
          className="w-16 rounded border border-line px-1.5 py-1 text-right"
        />
      </td>
      <td className="px-2 py-2 text-right">
        <input
          value={cost}
          onChange={(e) => setCost(e.target.value)}
          inputMode="decimal"
          placeholder="—"
          className="w-16 rounded border border-line px-1.5 py-1 text-right"
        />
      </td>
      <td className="px-2 py-2 text-right">
        {marginPct == null ? (
          <span className="text-ink-3">—</span>
        ) : (
          <span className={low ? 'font-extrabold text-red' : 'font-bold text-navy'}>
            {low && '⚠ '}
            {fmtMoney(priceC - costC)} · {marginPct.toFixed(0)}%
          </span>
        )}
      </td>
      <td className="px-2 py-2 text-center">
        <input
          type="checkbox"
          checked={row.available !== false}
          onChange={(e) => save({ available: e.target.checked })}
          disabled={busy}
        />
      </td>
      {dirty && (
        <td className="px-2 py-2">
          <button
            type="button"
            onClick={() => save({ price_cents: priceC, cost_cents: costC })}
            disabled={busy}
            className="rounded-lg bg-navy px-2 py-1 text-[11px] font-extrabold text-white disabled:opacity-50"
          >
            {busy ? '…' : 'Save'}
          </button>
        </td>
      )}
    </tr>
  )
}

function Card({ children }) {
  return <section className="rounded-2xl border border-line bg-white p-4">{children}</section>
}
